import { type System, ComponentStore } from '@ecs/world';
import { DEFAULT_TUNING, type Tuning } from '@game/tuning';
import type {
	InputResource,
	KinematicComponent,
	TimeScaleResource,
	VelocityComponent
} from '@game/scenes/characterPrototype/types';
import { clamp, lerp } from '@game/scenes/characterPrototype/math';

export function createInputSystem(
	inputKey: string,
	kinematics: ComponentStore<KinematicComponent>,
	velocities: ComponentStore<VelocityComponent>
): System {
	return ({ world }) => {
		const tuning = world.getResource<Tuning>('tuning') ?? DEFAULT_TUNING;
		const input = world.getResource<InputResource>(inputKey);
		if (!input) return;
		const magnitude = Math.min(Math.hypot(input.x, input.y), 1);
		const directionX = magnitude > 0 ? input.x / magnitude : 0;
		const timescale = world.getResource<TimeScaleResource>('timescale');
		if (timescale) {
			const deadzone = tuning.inputDeadzone;
			const scaledMag = magnitude <= deadzone ? 0 : (magnitude - deadzone) / (1 - deadzone);
			// Directional time scaling: pushing DOWN should accelerate time less than pushing UP/sides.
			const down01 = clamp(input.y, 0, 1); // joystick y is [-1..1], positive is down
			const downMul = clamp(tuning.slowmoDownMultiplier ?? 1, 0.05, 1);
			const effectiveMag = scaledMag * lerp(1, downMul, down01);
			timescale.base = lerp(tuning.slowmoMin, tuning.slowmoMax, clamp(effectiveMag, 0, 1));
		}

		kinematics.forEach((kinematic, entity) => {
			const velocity = velocities.get(entity);
			if (!velocity) return;
			const speed = kinematic.speed;
			velocity.x = directionX * speed;
		});
	};
}


